import { useCallback, useEffect, useState } from 'react';
import type {
  ZendeskAssignee,
  ZendeskComment,
  ZendeskIssueType,
  ZendeskListResponse
} from './zendesk';
import { mockAgents, mockFields, mockList, mockThread, zendeskCall } from './zendesk';

/*
 * React hooks over the Zendesk calls (see zendesk.ts). Tickets/agents/fields
 * load when the Comms tab mounts; the ticket thread loads when a ticket opens.
 * Mock vs real is decided by USE_MOCK in zendesk.ts.
 */

export interface ZendeskQuery<T> {
  data?: T;
  loading: boolean;
  error?: string;
  /** Re-runs the request — e.g. to pick up a ticket we just created. */
  reload: () => void;
}

const useAsync = <T>(run: () => Promise<T>, deps: unknown[], enabled = true): ZendeskQuery<T> => {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string>();
  // Bumped by reload() so the effect below re-runs with the same deps.
  const [nonce, setNonce] = useState(0);

  const reload = useCallback((): void => {
    setNonce((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const guard = { cancelled: false };

    setLoading(true);

    void (async (): Promise<void> => {
      try {
        const result = await run();

        if (!guard.cancelled) {
          setData(result);
          setError(undefined);
        }
      } catch (error_: unknown) {
        if (!guard.cancelled) {
          setError(error_ instanceof Error ? error_.message : 'Zendesk request failed');
        }
      } finally {
        if (!guard.cancelled) {
          setLoading(false);
        }
      }
    })();

    return (): void => {
      guard.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callers pass explicit deps; `run` changes each render
  }, [...deps, nonce]);

  return { data, loading, error, reload };
};

/*
 * Returns the whole list response rather than just `tickets`: `solvedFallback`
 * is the function telling us it found nothing open and fell back to recently
 * solved ones, which the list renders as a note.
 */
export const useZendeskTickets = (eid: string, cid: string): ZendeskQuery<ZendeskListResponse> =>
  useAsync(async () => zendeskCall({ action: 'list', eid, cid }, mockList), [eid, cid]);

export const useZendeskAgents = (): ZendeskQuery<ZendeskAssignee[]> =>
  useAsync(async () => (await zendeskCall({ action: 'agents' }, mockAgents)).assignees, []);

export const useZendeskFields = (): ZendeskQuery<ZendeskIssueType[]> =>
  useAsync(async () => (await zendeskCall({ action: 'fields' }, mockFields)).issueTypes, []);

export const useZendeskThread = (ticketId?: string): ZendeskQuery<ZendeskComment[]> =>
  useAsync(
    async () =>
      (await zendeskCall({ action: 'comments', ticketId: ticketId ?? '' }, () =>
        mockThread(ticketId ?? '')
      )).thread,
    [ticketId],
    ticketId !== undefined
  );
